import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useDesignation } from "../../contexts/DesignationsContext";
import Modal from "../../components/UI/Modal";
import DesignationForm from "../../components/Form/DesignationForm";
import ReusableTable from "../../components/UI/ReusableTable";
import DeleteButton from "../../components/UI/AlertDialog";
import Notification from "../../components/UI/Notification";
import { X, Briefcase, MoreVertical, Building, Calendar, Users, MapPin, Award, Clock, Grid } from 'lucide-react';
import DropdownMenu from "../../components/UI/DropdownMenu";

// Separate component for actions column to avoid hooks rule violation
const ActionsColumn = React.memo(({ row, openMenu, setOpenMenu, openModalForEdit, handleDeleteDesignation }) => {
  const buttonRef = useRef(null);
  
  return (
    <div className="flex items-center justify-center relative">
      <button
        ref={buttonRef}
        onClick={() =>
          setOpenMenu(openMenu === row.id ? null : row.id)
        }
        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
      >
        <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
      </button>

      {openMenu === row.id && (
        <DropdownMenu
          buttonRef={buttonRef}
          onClose={() => setOpenMenu(null)}
        >
          <button
            onClick={(e) => {
              openModalForEdit(row);
              setOpenMenu(null);
            }}
            className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
          >
            Edit
          </button>
          <DeleteButton
            style={`text-red-600 hover:text-red-800 px-3 py-2`}
            onDelete={(e) => {
              handleDeleteDesignation(row.id);
              setOpenMenu(null);
            }}
          />
        </DropdownMenu>
      )}
    </div>
  );
});

const DesignationMasters = React.memo(() => {
  const { designations, addDesignation, updateDesignation, deleteDesignation, isLoading } = useDesignation();
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDesignation, setEditingDesignation] = useState(null);
  const [message, setMessage] = useState(""); // Success message
  const [formErrors, setFormErrors] = useState({}); // Validation errors
  const [notification, setNotification] = useState(null);  
  const [openMenu, setOpenMenu] = useState(null);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true); // Ensures document.body is available
  }, []);


  
  // Handlers
  const openModalForAdd = useCallback(() => {
    setEditingDesignation(null);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const openModalForEdit = useCallback((designation) => {
    setEditingDesignation(designation);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingDesignation(null);
    setFormErrors({});
    setMessage("");
  }, []);

  const convertFormDataToObject = (formData) => {
    return Object.fromEntries(formData.entries());
  };

  const handleFormSubmit = useCallback(
    async (designation) => {
      setLoading(true);
      try {
        if (editingDesignation) {
          // console.log("designation payload", designation);
        
          // Convert FormData to object
          // console.log(designation instanceof FormData)
          const designationData = designation instanceof FormData ? convertFormDataToObject(designation) : designation;
  
          // console.log("Payload being sent:", { ...editingDesignation, ...designationData });
  
          await updateDesignation({ ...editingDesignation, ...designationData });
          setMessage("Designation updated successfully.");
          // setNotification("Designation updated successfully.");
          setNotification({
            message: "Designation updated successfully.",
            type: "success",
          });
        } else {
          // console.log("create designation", designation)
          await addDesignation(designation);
          setMessage("Designation added successfully.");
          // setNotification("Designation added successfully.");
          setNotification({
            message: "Designation added successfully.",
            type: "success",
          });
        }
        closeModal();
      } catch (err) {
        if (err.errors) {
          setFormErrors(err.errors);
        } else {
          // console.error("Unexpected error during submission:", err);
          // setNotification("Unexpected error during submission:", err);
          setNotification({
            message: `Unexpected error during submission:, ${err}`,
            type: "error",
          });
        }
      } finally {
        setLoading(false); 
      }
    },
    [editingDesignation, addDesignation, updateDesignation, closeModal]
  );

  // Memoize candidates with campaign names for the table
  const designationForTable = useMemo(
    () =>
      designations.map((designation) => ({
        ...designation,
        name: designation?.name || "Unknown Designation",
      })),
    [designations]
  );

  const handleDeleteDesignation = async (id) => {
    try {
      await deleteDesignation(id);
      // setNotification("Designation deleted successfully");
      setNotification({
        message: "Designation deleted successfully",
        type: "success",
      });
    } catch (error) {
      // setNotification("Failed to delete designation");
      setNotification({
        message: "Failed to delete designation",
        type: "error",
      });
    }
  };

  // Table columns
  const columns = useMemo(
    () => [
      { label: "Name", field: "name" },
      {
        label: "Actions",
        field: "actions",
        render: (row) => (
          <ActionsColumn
            row={row}
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
            openModalForEdit={openModalForEdit}
            handleDeleteDesignation={handleDeleteDesignation}
          />
        ),
      },
    ],
    [
      openMenu,
      openModalForEdit,
      handleDeleteDesignation,
    ]
  );
  
  return (
    <div className="px-3 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">
      
      {/* notification compo */}
      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-2xl font-semibold text-black dark:text-white">Designation Masters</h2>
        <button
          onClick={openModalForAdd}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 w-full sm:w-auto"
        >
          Add Designation
        </button>
      </div>

      {/* Success Message */}
      {message && (
        <div className="mb-4 p-4 bg-green-100 border border-green-500 text-green-700 rounded-md">
          {message}
        </div>
      )}

      {/* data table */}
      <div>
        <ReusableTable
          data={designationForTable}
          // data={requests.slice().reverse()}
          loading={isLoading}
          columns={columns}
          filterable={true}
          excludeFilterColumns={["actions"]}
          // excludeFilterColumns={excludeFilterColumns}
        />
      </div>

      {/* Modal for Adding or Editing */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        title={editingDesignation ? "Edit Designation" : "Add Designation"}
      >
        <DesignationForm
          loading={loading} 
          closeModal={closeModal}
          onSubmit={handleFormSubmit}
          initialValues={editingDesignation || { name: "", description: "" }}
          errors={formErrors}
        />
      </Modal>
    </div>
  );
});

export default DesignationMasters;
